import { LitElement, html, css, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { PreviewType } from "../lib/preview.js";

/**
 * ibcs-preview renders a small schematic IBCS chart for one of six `type`s
 * (waterfall | variance | column | line | integrated | table). It is the production
 * stand-in for the design's reusable preview component: an inline SVG
 * (viewBox 0 0 200 110) scaled to its container, whose fills/strokes reference the
 * chart tokens (--ink, --ink-soft, --pos, --neg) so it re-themes automatically.
 *
 * IBCS conventions preserved per the handoff: solid `ink` actuals, `ink-soft`
 * outlines/forecast dashes, and red/green only for variance. In production these
 * schematics would be replaced by real rendered Bino report thumbnails at the same
 * sizes.
 */
@customElement("ibcs-preview")
export class IbcsPreview extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      color: var(--ink);
    }
    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  `;

  @property() type: PreviewType = "column";

  private chart(): TemplateResult {
    switch (this.type) {
      case "waterfall":
        return html`<svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <line x1="6" y1="96" x2="194" y2="96" style="stroke:var(--ink)" stroke-width="1"></line>
          <line x1="28" y1="26" x2="39" y2="26" style="stroke:var(--text-faint)" stroke-width="1" stroke-dasharray="2 2"></line>
          <line x1="59" y1="8" x2="70" y2="8" style="stroke:var(--text-faint)" stroke-width="1" stroke-dasharray="2 2"></line>
          <line x1="90" y1="20" x2="101" y2="20" style="stroke:var(--text-faint)" stroke-width="1" stroke-dasharray="2 2"></line>
          <line x1="121" y1="6" x2="132" y2="6" style="stroke:var(--text-faint)" stroke-width="1" stroke-dasharray="2 2"></line>
          <line x1="152" y1="16" x2="163" y2="16" style="stroke:var(--text-faint)" stroke-width="1" stroke-dasharray="2 2"></line>
          <rect x="8" y="26" width="20" height="70" style="fill:var(--ink)"></rect>
          <rect x="39" y="8" width="20" height="18" style="fill:var(--pos)"></rect>
          <rect x="70" y="8" width="20" height="12" style="fill:var(--neg)"></rect>
          <rect x="101" y="6" width="20" height="14" style="fill:var(--pos)"></rect>
          <rect x="132" y="6" width="20" height="10" style="fill:var(--neg)"></rect>
          <rect x="163" y="16" width="20" height="80" style="fill:var(--ink)"></rect>
        </svg>`;
      case "variance":
        return html`<svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <rect x="8" y="30" width="14" height="28" style="fill:var(--pos)"></rect>
          <rect x="31" y="38" width="14" height="20" style="fill:var(--pos)"></rect>
          <rect x="54" y="58" width="14" height="14" style="fill:var(--neg)"></rect>
          <rect x="77" y="24" width="14" height="34" style="fill:var(--pos)"></rect>
          <rect x="100" y="58" width="14" height="22" style="fill:var(--neg)"></rect>
          <rect x="123" y="42" width="14" height="16" style="fill:var(--pos)"></rect>
          <rect x="146" y="28" width="14" height="30" style="fill:var(--pos)"></rect>
          <rect x="169" y="58" width="14" height="10" style="fill:var(--neg)"></rect>
          <line x1="4" y1="58" x2="196" y2="58" style="stroke:var(--ink)" stroke-width="1.25"></line>
        </svg>`;
      case "column":
        return html`<svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <line x1="6" y1="96" x2="194" y2="96" style="stroke:var(--ink)" stroke-width="1"></line>
          <rect x="14" y="56" width="20" height="40" style="fill:var(--ink)"></rect>
          <rect x="45" y="44" width="20" height="52" style="fill:var(--ink)"></rect>
          <rect x="76" y="48" width="20" height="48" style="fill:var(--ink)"></rect>
          <rect x="107" y="32" width="20" height="64" style="fill:var(--ink)"></rect>
          <rect x="138" y="38" width="20" height="58" style="fill:var(--ink)"></rect>
          <rect x="169" y="24" width="20" height="72" style="fill:var(--ink)"></rect>
          <line x1="12" y1="50" x2="36" y2="50" style="stroke:var(--ink)" stroke-width="2"></line>
          <line x1="43" y1="40" x2="67" y2="40" style="stroke:var(--ink)" stroke-width="2"></line>
          <line x1="74" y1="52" x2="98" y2="52" style="stroke:var(--ink)" stroke-width="2"></line>
          <line x1="105" y1="36" x2="129" y2="36" style="stroke:var(--ink)" stroke-width="2"></line>
          <line x1="136" y1="30" x2="160" y2="30" style="stroke:var(--ink)" stroke-width="2"></line>
          <line x1="167" y1="30" x2="191" y2="30" style="stroke:var(--ink)" stroke-width="2"></line>
        </svg>`;
      case "line":
        return html`<svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <line x1="6" y1="96" x2="194" y2="96" style="stroke:var(--ink)" stroke-width="1"></line>
          <polyline points="12,72 46,60 80,66 114,46 130,52" fill="none" style="stroke:var(--ink)" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"></polyline>
          <polyline points="130,52 164,38 192,26" fill="none" style="stroke:var(--ink-soft)" stroke-width="2.5" stroke-dasharray="4 3" stroke-linecap="round"></polyline>
          <circle cx="12" cy="72" r="2.6" style="fill:var(--ink)"></circle>
          <circle cx="46" cy="60" r="2.6" style="fill:var(--ink)"></circle>
          <circle cx="80" cy="66" r="2.6" style="fill:var(--ink)"></circle>
          <circle cx="114" cy="46" r="2.6" style="fill:var(--ink)"></circle>
          <circle cx="130" cy="52" r="3.2" style="fill:var(--ink)"></circle>
          <circle cx="164" cy="38" r="2.6" style="fill:var(--surface);stroke:var(--ink-soft)" stroke-width="1.5"></circle>
          <circle cx="192" cy="26" r="2.6" style="fill:var(--surface);stroke:var(--ink-soft)" stroke-width="1.5"></circle>
        </svg>`;
      case "integrated":
        return html`<svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <line x1="6" y1="96" x2="194" y2="96" style="stroke:var(--ink)" stroke-width="1"></line>
          <rect x="14" y="54" width="30" height="42" fill="none" style="stroke:var(--ink-soft)" stroke-width="1.5"></rect>
          <rect x="22" y="46" width="16" height="50" style="fill:var(--ink)"></rect>
          <line x1="14" y1="42" x2="44" y2="42" style="stroke:var(--ink)" stroke-width="2"></line>
          <rect x="59" y="60" width="30" height="36" fill="none" style="stroke:var(--ink-soft)" stroke-width="1.5"></rect>
          <rect x="67" y="40" width="16" height="56" style="fill:var(--ink)"></rect>
          <line x1="59" y1="36" x2="89" y2="36" style="stroke:var(--ink)" stroke-width="2"></line>
          <rect x="104" y="64" width="30" height="32" fill="none" style="stroke:var(--ink-soft)" stroke-width="1.5"></rect>
          <rect x="112" y="52" width="16" height="44" style="fill:var(--ink)"></rect>
          <line x1="104" y1="50" x2="134" y2="50" style="stroke:var(--ink)" stroke-width="2"></line>
          <rect x="149" y="48" width="30" height="48" fill="none" style="stroke:var(--ink-soft)" stroke-width="1.5"></rect>
          <rect x="157" y="30" width="16" height="66" style="fill:var(--ink)"></rect>
          <line x1="149" y1="26" x2="179" y2="26" style="stroke:var(--ink)" stroke-width="2"></line>
        </svg>`;
      case "table":
        return html`<svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <rect x="10" y="12" width="62" height="6" rx="1" style="fill:var(--ink-soft)"></rect>
          <rect x="118" y="12" width="34" height="6" rx="1" style="fill:var(--text-faint)"></rect>
          <rect x="174" y="11" width="8" height="8" style="fill:var(--pos)"></rect>
          <rect x="10" y="30" width="52" height="6" rx="1" style="fill:var(--ink-soft)"></rect>
          <rect x="124" y="30" width="28" height="6" rx="1" style="fill:var(--text-faint)"></rect>
          <rect x="174" y="29" width="8" height="8" style="fill:var(--neg)"></rect>
          <rect x="10" y="48" width="58" height="6" rx="1" style="fill:var(--ink-soft)"></rect>
          <rect x="120" y="48" width="32" height="6" rx="1" style="fill:var(--text-faint)"></rect>
          <rect x="174" y="47" width="8" height="8" style="fill:var(--pos)"></rect>
          <line x1="10" y1="66" x2="190" y2="66" style="stroke:var(--ink)" stroke-width="1"></line>
          <rect x="10" y="74" width="46" height="7" rx="1" style="fill:var(--ink)"></rect>
          <rect x="116" y="74" width="36" height="7" rx="1" style="fill:var(--ink)"></rect>
          <rect x="174" y="73" width="8" height="9" style="fill:var(--pos)"></rect>
          <line x1="10" y1="92" x2="190" y2="92" style="stroke:var(--ink)" stroke-width="1"></line>
          <line x1="10" y1="95" x2="190" y2="95" style="stroke:var(--ink)" stroke-width="1"></line>
        </svg>`;
    }
  }

  override render() {
    return this.chart();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ibcs-preview": IbcsPreview;
  }
}
